/**
 * The transmission log table, sheet 32 callouts 4, 5, 6, 8, 9 and 10 (mission 7
 * WP11 T057, T058; FR-014, FR-028, NFR-008; TX.6, TX.8, ANA.11, NF.7; OR-5,
 * OI-4; squad U5, U10, P14; C-004 (A.3 as amended), C-008; style guide §7.7,
 * §7.13, §9; contracts/ui-api.md "Sheet 32").
 *
 * Presentational, one page of at most 50 rows in the API's order (newest
 * first). The eight callout 4 columns, headed by `logCol.*` in `<th scope>`:
 * the time in `<time dateTime>` (mono, UTC, stated once in the caption), the
 * acting user, the run's short id in mono linking to the run, the bureau, the
 * bytes actually sent (mono, right-aligned, 0 on a failed attempt), the remote
 * filename in mono, the result badge with its `detail.*` line, and the actions.
 * The result cell is a polite live region, so a row that resolves while the
 * page polls is announced.
 *
 * The actions follow OR-5 and OI-4, one table (`actionsFor`):
 * - success: Report rejection, opening sheet 23 with `?transmission=`;
 * - timeout (either detail): Report rejection, and Retry behind a §7.13
 *   confirm step that says the bureau may already have the file. The step
 *   records and sends nothing; only its confirm navigates to the transmit
 *   page with the bureau preselected, where the transmit is made (TX.8: free);
 * - auth_failed and not_sent/credential-changed: Test connection on sheet 41
 *   at that bureau's card (`#<bureau>`), never transmit;
 * - not_sent/host-refused: Edit the credentials, at the same card;
 * - not_sent/mgts-unavailable and not_sent/not-attempted: Retry, straight to
 *   the transmit page;
 * - not_sent/bureau-disabled, not_sent/file-unavailable and pending: nothing.
 *
 * Not built, and no space kept for them (C-008, C-004): CSV export, the
 * acknowledgment column and "View ack", the Auditor view, "Ask an Owner".
 *
 * Mission 9 WP14 T091 (32/6; FR-016's UI half; NF.9, TX.6; research §1.23): a resolved row's result
 * cell adds `transmissionEmailNote`, since each resolution writes one `transmission_result` email.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'

import { TransmissionResultBadge } from '@/components/trellis/TransmissionResultBadge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { bureauName } from '@/lib/api/bureaus'
import type { TransmissionDetail, TransmissionEntry } from '@/lib/api/transmissions'
import { Mono, count, date, isoDateTime, shortId, time } from '@/lib/format'
import { copyText, type TextKey } from '@/lib/provisional-copy'

const COLUMNS: ReadonlyArray<{ key: TextKey; numeric?: true }> = [
  { key: 'logCol.time' },
  { key: 'logCol.acting' },
  { key: 'logCol.run' },
  { key: 'logCol.bureau' },
  { key: 'logCol.bytes', numeric: true },
  { key: 'logCol.remoteFilename' },
  { key: 'logCol.result' },
  { key: 'logCol.actions' },
]

const DETAIL: Record<TransmissionDetail, TextKey> = {
  'auth-rejected': 'detail.authRejected',
  'host-timeout': 'detail.hostTimeout',
  interrupted: 'detail.interrupted',
  'mgts-unavailable': 'detail.mgtsUnavailable',
  'credential-changed': 'detail.credentialChanged',
  'file-unavailable': 'detail.fileUnavailable',
  'bureau-disabled': 'detail.bureauDisabled',
  'host-refused': 'detail.hostRefused',
  'not-attempted': 'detail.notAttempted',
}

type Action = 'reportRejection' | 'retryConfirm' | 'retry' | 'testConnection' | 'editCredentials'

/** OR-5 and OI-4: what each result and detail offers, in display order. */
export function actionsFor(entry: TransmissionEntry): Action[] {
  switch (entry.result) {
    case 'success':
      return ['reportRejection']
    case 'timeout':
      return ['reportRejection', 'retryConfirm']
    case 'auth_failed':
      return ['testConnection']
    case 'not_sent':
      if (entry.detail === 'credential-changed') return ['testConnection']
      if (entry.detail === 'host-refused') return ['editCredentials']
      if (entry.detail === 'mgts-unavailable' || entry.detail === 'not-attempted') return ['retry']
      return []
    case 'pending':
      return []
  }
}

const LINK =
  'text-body-sm text-[var(--color-link)] underline-offset-4 hover:text-[var(--color-link-hover)] hover:underline'

function runPath(entry: TransmissionEntry): string {
  return `/app/runs/${encodeURIComponent(entry.run_id)}`
}

function transmitPath(entry: TransmissionEntry): string {
  return `${runPath(entry)}/transmit?${new URLSearchParams({ bureau: entry.bureau }).toString()}`
}

function bureauCard(entry: TransmissionEntry): string {
  return `/app/settings/bureaus#${entry.bureau}`
}

/** Retry on a timeout row (OR-5): the bureau may already have the file, so the reporter confirms first. */
function RetryConfirm({ entry }: { entry: TransmissionEntry }) {
  const navigate = useNavigate()
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link" size="sm">
          {copyText('retryTransmission')}
        </Button>
      </DialogTrigger>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{copyText('retryConfirmTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>{copyText('retryConfirmBody')}</DialogDescription>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost">{copyText('modalCancel')}</Button>
          </DialogClose>
          <Button onClick={() => navigate(transmitPath(entry))}>{copyText('retryConfirmAction')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ActionControl({ action, entry }: { action: Action; entry: TransmissionEntry }) {
  switch (action) {
    case 'reportRejection':
      return (
        <Link
          to={`${runPath(entry)}/report-rejection?${new URLSearchParams({ transmission: entry.transmission_id }).toString()}`}
          className={LINK}
        >
          {copyText('reportRejectionRow')}
        </Link>
      )
    case 'retryConfirm':
      return <RetryConfirm entry={entry} />
    case 'retry':
      return (
        <Link to={transmitPath(entry)} className={LINK}>
          {copyText('retryTransmission')}
        </Link>
      )
    case 'testConnection':
      return (
        <Link to={bureauCard(entry)} className={LINK}>
          {copyText('testConnectionFirst')}
        </Link>
      )
    case 'editCredentials':
      return (
        <Link to={bureauCard(entry)} className={LINK}>
          {copyText('editCredentials')}
        </Link>
      )
  }
}

function LogRow({ entry }: { entry: TransmissionEntry }) {
  return (
    <TableRow data-transmission-row={entry.transmission_id}>
      <TableCell className="whitespace-nowrap">
        <time dateTime={isoDateTime(entry.attempted_at)} className="font-mono text-body-sm">
          {date(entry.attempted_at)} {time(entry.attempted_at)}
        </time>
      </TableCell>
      <TableCell className="max-w-[14rem] truncate text-body-sm" title={entry.acting_email}>
        {entry.acting_email}
      </TableCell>
      <TableCell>
        <Link to={runPath(entry)} title={entry.run_id} className={LINK}>
          <Mono>{shortId(entry.run_id)}</Mono>
        </Link>
      </TableCell>
      <TableCell className="whitespace-nowrap">{bureauName(entry.bureau)}</TableCell>
      <TableCell numeric>{count(entry.bytes_sent)}</TableCell>
      <TableCell className="max-w-[16rem]">
        <Mono title={entry.remote_filename} className="block truncate text-body-sm">
          {entry.remote_filename}
        </Mono>
      </TableCell>
      <TableCell className="py-2">
        <div aria-live="polite" aria-atomic="true" className="flex flex-col items-start gap-1">
          <TransmissionResultBadge result={entry.result} />
          {entry.detail ? <p className="text-caption text-navy-600">{copyText(DETAIL[entry.detail])}</p> : null}
          {/* Mission 9 WP14 T091 (32/6; FR-016; NF.9): the resolution also writes the transmission_result email. */}
          {entry.result !== 'pending' ? <p className="text-caption text-navy-600">{copyText('transmissionEmailNote')}</p> : null}
        </div>
      </TableCell>
      <TableCell data-log-actions="" className="whitespace-nowrap">
        {actionsFor(entry).length > 0 ? (
          <span className="flex items-center gap-3">
            {actionsFor(entry).map((action) => (
              <ActionControl key={action} action={action} entry={entry} />
            ))}
          </span>
        ) : null}
      </TableCell>
    </TableRow>
  )
}

export interface LogPaging {
  /** Rows before this page, for the footer range. */
  offset: number
  hasPrevious: boolean
  hasNext: boolean
  onPrevious(): void
  onNext(): void
}

export interface LogTableProps {
  entries: TransmissionEntry[]
  loading: boolean
  /** `logEmpty`, or `logEmptyFiltered` when a filter is set. */
  emptyText: string
  paging: LogPaging
}

function Footer({ paging, rows }: { paging: LogPaging; rows: number }) {
  return (
    <div data-log-footer="" className="mt-3 flex items-center justify-between text-caption text-navy-600">
      <Mono>{rows > 0 ? `${paging.offset + 1}–${paging.offset + rows}` : null}</Mono>
      {paging.hasPrevious || paging.hasNext ? (
        <div className="flex items-center gap-2">
          {paging.hasPrevious ? (
            <Button variant="secondary" size="sm" icon={<ChevronLeft aria-hidden />} onClick={paging.onPrevious}>
              {copyText('previousPage')}
            </Button>
          ) : null}
          {paging.hasNext ? (
            <Button variant="secondary" size="sm" onClick={paging.onNext}>
              {copyText('nextPage')}
              <ChevronRight aria-hidden />
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

export function LogTable({ entries, loading, emptyText, paging }: LogTableProps) {
  return (
    <div>
      <Table>
        <TableCaption>{copyText('timesInUtc')}</TableCaption>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {COLUMNS.map((c) => (
              <TableHead key={c.key} className={c.numeric ? 'text-right' : undefined}>
                {copyText(c.key)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading ? (
            <TableSkeletonRows columns={COLUMNS.length} />
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMNS.length} className="text-center text-body text-navy-600">
                {emptyText}
              </TableCell>
            </TableRow>
          ) : (
            entries.map((entry) => <LogRow key={entry.transmission_id} entry={entry} />)
          )}
        </TableBody>
      </Table>
      {!loading && (entries.length > 0 || paging.hasPrevious) ? <Footer paging={paging} rows={entries.length} /> : null}
    </div>
  )
}
